/**
 * Lỗi API — mã lỗi, câu hành động cho từng mã, và phép đổi lỗi bất kỳ thành
 * tiếng Việt.
 *
 * Hợp đồng lỗi của server (docs/05 §5.3) luôn là:
 *   { "error": { "code": "...", "message": "...", "detail": {} } }
 *
 * Client xử lý theo MÃ, KHÔNG BAO GIỜ so khớp chuỗi thông điệp.
 */

/** Mã lỗi — docs/05 §5.3. */
export type MaLoi =
  | 'NONCE_REPLAYED' | 'NONCE_EXPIRED' | 'BAD_SIGNATURE'
  | 'DEVICE_NOT_BOUND' | 'DEVICE_MISMATCH'
  | 'OUT_OF_GEOFENCE' | 'SITE_UNKNOWN' | 'MOCK_LOCATION'
  | 'OTP_INVALID' | 'DUPLICATE_PUNCH' | 'RATE_LIMITED' | 'PHOTO_REQUIRED'
  | 'ACCOUNT_LOCKED' | 'FORBIDDEN' | 'NOT_FOUND' | 'VALIDATION_ERROR'
  | 'CONSENT_REQUIRED' | 'FACE_REJECTED' | 'FACE_MISMATCH' | 'FACE_NOT_ENROLLED'
  | 'FACE_VERIFY_REQUIRED' | 'FACE_TEMPLATES_FULL' | 'FACE_SERVICE_UNAVAILABLE'
  | 'PASSWORD_CHANGE_REQUIRED' | 'PASSWORD_TOO_WEAK' | 'PASSWORD_REUSED'

export class ApiError extends Error {
  readonly code: MaLoi | string
  readonly status: number
  readonly detail: Record<string, unknown>

  constructor(
    code: MaLoi | string,
    message: string,
    status: number,
    detail: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.detail = detail
  }
}

/**
 * Việc NHÂN VIÊN phải làm cho từng mã lỗi (cột "Hành động phía client" docs/05
 * §5.3). Để ở một chỗ để màn hình nào cũng cư xử giống nhau.
 *
 * Câu chữ ở đây viết cho người đang đứng ở công trường, có thể đeo găng tay,
 * dưới nắng, và cần biết BẤM GÌ TIẾP — không phải cho lập trình viên.
 */
export const HANH_DONG_LOI: Record<string, string> = {
  NONCE_REPLAYED: 'Phiên chấm công đã hết hiệu lực. Bấm lại nút chấm công.',
  NONCE_EXPIRED: 'Quá thời gian cho phép. Bấm lại nút chấm công.',
  BAD_SIGNATURE: 'Không xác thực được mạng công ty. Đang thử cách khác…',
  DEVICE_NOT_BOUND: 'Thiết bị chưa đăng ký. Vào mục Cá nhân để đăng ký.',
  DEVICE_MISMATCH: 'Đây không phải điện thoại bạn đã đăng ký. Cần quản lý duyệt đổi máy.',
  OUT_OF_GEOFENCE: 'Bạn đang ở ngoài phạm vi cho phép.',
  SITE_UNKNOWN: 'Chưa xác định được công trình. Bản ghi sẽ chờ quản lý xác nhận.',
  MOCK_LOCATION: 'Phát hiện ứng dụng giả mạo vị trí. Hãy tắt ứng dụng đó rồi thử lại.',
  OTP_INVALID: 'Mã không đúng hoặc đã hết hạn. Lấy mã mới rồi nhập lại.',
  RATE_LIMITED: 'Bạn thao tác quá nhanh. Chờ một lát rồi thử lại.',
  PHOTO_REQUIRED: 'Địa điểm này bắt buộc chụp ảnh xác minh. Chấm công lại và chụp mặt.',
  ACCOUNT_LOCKED: 'Tài khoản tạm khoá do sai mật khẩu nhiều lần. Liên hệ quản lý.',
  PASSWORD_CHANGE_REQUIRED: 'Bạn cần đổi mật khẩu trước khi dùng hệ thống.',
  PASSWORD_TOO_WEAK: 'Mật khẩu chưa đủ mạnh. Xem hướng dẫn bên dưới ô nhập.',
  PASSWORD_REUSED: 'Mật khẩu mới trùng mật khẩu đang dùng. Chọn mật khẩu khác.',
  // Cổng Face ID (docs/07) — bản ghi đã bị từ chối, phải chấm công LẠI
  FACE_REJECTED: 'Ảnh chưa đạt: đưa mặt vào giữa khung, đủ sáng, giữ máy chắc tay rồi chấm công lại.',
  FACE_MISMATCH: 'Khuôn mặt không khớp mẫu đã đăng ký. Bỏ khẩu trang, kính râm rồi chấm công lại.',
  FACE_NOT_ENROLLED: 'Bạn chưa đăng ký khuôn mặt. Vào Cá nhân → Khuôn mặt để đăng ký, rồi chấm công lại.',
  FACE_VERIFY_REQUIRED: 'Địa điểm này bắt buộc xác minh khuôn mặt ngay. Kiểm tra mạng rồi chấm công lại.',
  CONSENT_REQUIRED: 'Cần ký đồng ý xử lý dữ liệu sinh trắc học trước. Vào Cá nhân → Khuôn mặt.',
  FACE_SERVICE_UNAVAILABLE: 'Dịch vụ nhận diện tạm gián đoạn. Thử lại sau ít phút.',
  FACE_TEMPLATES_FULL: 'Đã đủ 3 mẫu khuôn mặt. Thu hồi mẫu cũ nếu muốn đăng ký lại.',
  FORBIDDEN: 'Bạn không có quyền thực hiện thao tác này.',
  NOT_FOUND: 'Không tìm thấy dữ liệu này.',
  VALIDATION_ERROR: 'Thông tin nhập chưa hợp lệ. Kiểm tra lại các ô đã đánh dấu.',
}

/**
 * Mã lỗi do cổng Face ID chặn: server ĐÃ lưu bản ghi với `status=rejected`,
 * nhân viên phải chấm công lại (`client_uuid` mới), KHÔNG thử gửi lại bản cũ.
 */
export const MA_LOI_FACE: ReadonlySet<string> = new Set([
  'PHOTO_REQUIRED', 'FACE_REJECTED', 'FACE_MISMATCH', 'FACE_NOT_ENROLLED',
  'FACE_VERIFY_REQUIRED',
])

/**
 * Đổi một lỗi BẤT KỲ thành câu người đọc được.
 *
 * Hàng rào cuối chặn `[object Object]`, `undefined`, `Failed to fetch` và vệt
 * stack lọt ra giao diện (mục XIV của đề bài).
 */
export function thongDiepLoi(loi: unknown): string {
  if (loi instanceof ApiError) {
    return HANH_DONG_LOI[loi.code] ?? loi.message ?? 'Có lỗi xảy ra, thử lại sau.'
  }

  // `fetch` ném `TypeError` khi không dựng nổi kết nối: mất sóng, sai tên
  // miền, chứng chỉ TLS không tin cậy. "Failed to fetch" vô nghĩa với thợ.
  if (loi instanceof TypeError) {
    return 'Không kết nối được máy chủ. Kiểm tra sóng rồi thử lại.'
  }

  if (loi instanceof DOMException && loi.name === 'AbortError') {
    return 'Chờ quá lâu không có phản hồi. Thử lại.'
  }

  if (loi instanceof Error && loi.message) return loi.message
  if (typeof loi === 'string' && loi.trim()) return loi

  return 'Có lỗi xảy ra. Thử lại, nếu vẫn lỗi thì báo quản lý.'
}

/** Mã lỗi, để giao diện phân nhánh — `null` nếu không phải lỗi từ API. */
export function maLoi(loi: unknown): string | null {
  return loi instanceof ApiError ? loi.code : null
}
